"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useState, useRef, useEffect } from "react";
import { useSearchParams, useRouter } from "next/navigation";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";

import Link from "next/link";
import { FcGoogle } from "react-icons/fc";
import { cn } from "@/lib/utils";
import Password from "./password";
import { Button } from "./button";
import { Logo } from "./Logo";
import { send2FACodeAction, verify2FACodeAction, resend2FACodeAction } from "@/app/actions/two-factor";

const formSchema = z.object({
  email: z
    .string({
      required_error: "Please enter email",
    })
    .email("Please enter valid email")
    .min(1, "Please enter email"),
  password: z
    .string({
      required_error: "Please enter password",
    })
    .min(1, "Please enter password"),
});

export type LoginUser = z.infer<typeof formSchema>;

export function LoginForm() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState(["", "", "", "", "", ""]);
  const [credentials, setCredentials] = useState<LoginUser | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const form = useForm<LoginUser>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  // Handle Google OAuth errors from URL params
  useEffect(() => {
    const googleError = searchParams.get("error");
    if (googleError) {
      const errorMessages: Record<string, string> = {
        google_denied: "Google sign-in was cancelled",
        no_code: "Google sign-in failed - no authorization code",
        config_error: "Server configuration error",
        token_exchange_failed: "Failed to complete Google sign-in",
        user_info_failed: "Failed to get user information from Google",
        auth_failed: "Authentication failed",
        unexpected_error: "An unexpected error occurred",
      };
      setError(errorMessages[googleError] || "Google sign-in failed");
      // Clean up URL
      window.history.replaceState({}, "", "/login");
    }
  }, [searchParams]);

  async function onSubmit(values: LoginUser) {
    setIsLoading(true);
    setError(null);

    try {
      const result = await send2FACodeAction(values.email, values.password);

      if (!result.success) {
        setError(result.error || "Login failed");
        return;
      }

      // Email not verified - redirect to verify page
      if (result.requiresVerification && result.email) {
        router.push(`/verify-email?email=${encodeURIComponent(result.email)}`);
        return;
      }

      // 2FA disabled - redirect directly to CRM
      if (!result.requires2FA && result.redirectUrl) {
        window.location.href = result.redirectUrl;
        return;
      }

      // 2FA is required - store credentials for resend and show verification
      setCredentials(values);
      setShowVerification(true);
      setResendCooldown(60);
      startCooldownTimer();
    } catch (e) {
      setError("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  }

  function startCooldownTimer() {
    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  async function handleResendCode() {
    if (!credentials || resendCooldown > 0) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await resend2FACodeAction(credentials.email, credentials.password);

      if (!result.success) {
        setError(result.error || "Failed to resend code");
        return;
      }

      setResendCooldown(60);
      startCooldownTimer();
      setVerificationCode(["", "", "", "", "", ""]);
    } catch (e) {
      setError("Failed to resend code");
    } finally {
      setIsLoading(false);
    }
  }

  function handleCodeChange(index: number, value: string) {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...verificationCode];
    newCode[index] = value.slice(-1);
    setVerificationCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits are entered
    if (newCode.every((digit) => digit !== "") && newCode.join("").length === 6) {
      handleVerifyCode(newCode.join(""));
    }
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !verificationCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  }

  function handlePaste(e: React.ClipboardEvent) {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pastedData.length === 6) {
      const newCode = pastedData.split("");
      setVerificationCode(newCode);
      handleVerifyCode(pastedData);
    }
  }

  async function handleVerifyCode(code: string) {
    if (!credentials) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await verify2FACodeAction(credentials.email, code);

      if (!result.success) {
        setError(result.error || "Invalid verification code");
        setVerificationCode(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
        return;
      }

      if (result.redirectUrl) {
        window.location.href = result.redirectUrl;
      }
    } catch (e) {
      setError("An unexpected error occurred");
      setVerificationCode(["", "", "", "", "", ""]);
    } finally {
      setIsLoading(false);
    }
  }

  function handleBackToLogin() {
    setShowVerification(false);
    setVerificationCode(["", "", "", "", "", ""]);
    setCredentials(null);
    setError(null);
  }

  // 2FA Verification Screen
  if (showVerification) {
    return (
      <div className="flex items-center w-full justify-center px-4 py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-md">
          <div>
            <div className="flex">
              <Logo />
            </div>
            <h2 className="mt-8 text-2xl font-bold leading-9 tracking-tight text-black dark:text-white">
              Enter verification code
            </h2>
            <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
              We sent a 6-digit code to{" "}
              <span className="font-medium text-black dark:text-white">
                {credentials?.email}
              </span>
            </p>
          </div>

          <div className="mt-10">
            {error && (
              <div className="mb-4 p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
                {error}
              </div>
            )}

            <div className="flex justify-center gap-2 mb-6" onPaste={handlePaste}>
              {verificationCode.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => { inputRefs.current[index] = el; }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  disabled={isLoading}
                  className="w-12 h-14 text-center text-2xl font-bold bg-white dark:bg-neutral-900 rounded-md border-0 shadow-aceternity text-black dark:text-white focus:ring-2 focus:ring-neutral-400 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  autoFocus={index === 0}
                />
              ))}
            </div>

            <Button
              className="w-full"
              disabled={isLoading || verificationCode.some((d) => d === "")}
              onClick={() => handleVerifyCode(verificationCode.join(""))}
            >
              {isLoading ? "Verifying..." : "Verify"}
            </Button>

            <div className="mt-6 text-center">
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                Didn&apos;t receive the code?{" "}
                {resendCooldown > 0 ? (
                  <span className="text-neutral-500">
                    Resend in {resendCooldown}s
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={isLoading}
                    className="text-black dark:text-white font-medium hover:underline disabled:opacity-50"
                  >
                    Resend code
                  </button>
                )}
              </p>
            </div>

            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={handleBackToLogin}
                className="text-sm text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
              >
                Back to sign in
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Login Form
  return (
    <Form {...form}>
      <div className="flex items-center w-full justify-center px-4 py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-md">
          <div>
            <div className="flex">
              <Logo />
            </div>
            <h2 className="mt-8 text-2xl font-bold leading-9 tracking-tight text-black dark:text-white">
              Sign in to your account
            </h2>
          </div>

          <div className="mt-10">
            <div>
              {error && (
                <div className="mb-4 p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
                  {error}
                </div>
              )}

              {/* Google Sign-In Button */}
              <Button
                as={Link}
                href="/api/auth/google"
                variant="outline"
                className="w-full gap-3 mb-6"
              >
                <FcGoogle className="w-5 h-5" />
                Continue with Google
              </Button>

              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-neutral-300 dark:border-neutral-700" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white dark:bg-neutral-950 px-2 text-neutral-500">
                    or continue with email
                  </span>
                </div>
              </div>

              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6"
              >
                <div>
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium leading-6 text-neutral-700 dark:text-muted-dark"
                        >
                          Email address
                        </label>
                        <FormControl>
                          <div className="mt-2">
                            <input
                              id="email"
                              type="email"
                              placeholder="Enter your email"
                              disabled={isLoading}
                              className="block w-full bg-white dark:bg-neutral-900 px-4 rounded-md border-0 py-1.5 shadow-aceternity text-black placeholder:text-gray-400 focus:ring-2 focus:ring-neutral-400 focus:outline-none sm:text-sm sm:leading-6 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                              {...field}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div>
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <label
                          htmlFor="password"
                          className="block text-sm font-medium leading-6 text-neutral-700 dark:text-muted-dark"
                        >
                          Password
                        </label>
                        <FormControl>
                          <div className="mt-2">
                            <Password
                              id="password"
                              type="password"
                              placeholder="Enter your password"
                              disabled={isLoading}
                              className="block w-full bg-white dark:bg-neutral-900 px-4 rounded-md border-0 py-1.5 shadow-aceternity text-black placeholder:text-gray-400 focus:ring-2 focus:ring-neutral-400 focus:outline-none sm:text-sm sm:leading-6 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                              {...field}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex items-center justify-end">
                  <Link
                    href="/forgot-password"
                    className="text-sm text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
                  >
                    Forgot password?
                  </Link>
                </div>

                <div>
                  <Button className="w-full" disabled={isLoading}>
                    {isLoading ? "Signing in..." : "Sign in"}
                  </Button>
                  <p
                    className={cn(
                      "text-sm text-neutral-500 text-center mt-4 text-muted dark:text-muted-dark"
                    )}
                  >
                    Don&apos;t have an account?{" "}
                    <Link href="/signup" className="text-black dark:text-white">
                      Sign up
                    </Link>
                  </p>
                </div>
              </form>
            </div>

            <p className="text-neutral-600 dark:text-neutral-400 text-sm text-center mt-8">
              By clicking on sign in, you agree to our{" "}
              <Link
                href="#"
                className="text-neutral-500 dark:text-neutral-300"
              >
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link
                href="#"
                className="text-neutral-500 dark:text-neutral-300"
              >
                Privacy Policy
              </Link>
            </p>
          </div>
        </div>
      </div>
    </Form>
  );
}
